'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sparkles, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, Globe, Calendar } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function AstrolojikYayilimPage() {
  const astroHouses = [
    { house: 1, name: "Kimlik", description: "Kişiliğiniz, nasıl görünüyorsunuz", sign: "Koç" },
    { house: 2, name: "Değerler", description: "Para, mülk, özgüven", sign: "Boğa" },
    { house: 3, name: "İletişim", description: "Kardeşler, yakın çevre, öğrenme", sign: "İkizler" },
    { house: 4, name: "Ev & Aile", description: "Kökenler, güvenlik, ev hayatı", sign: "Yengeç" },
    { house: 5, name: "Yaratıcılık", description: "Aşk, çocuklar, hobi, eğlence", sign: "Aslan" },
    { house: 6, name: "Sağlık & İş", description: "Günlük rutin, sağlık, hizmet", sign: "Başak" },
    { house: 7, name: "Ortaklık", description: "Evlilik, iş ortaklığı, açık düşmanlar", sign: "Terazi" },
    { house: 8, name: "Dönüşüm", description: "Ölüm-doğum, ortak kaynaklar, gizem", sign: "Akrep" },
    { house: 9, name: "Felsefe", description: "Yükseköğrenim, seyahat, din", sign: "Yay" },
    { house: 10, name: "Kariyer", description: "Meslek, ün, sosyal statü", sign: "Oğlak" },
    { house: 11, name: "Arkadaşlık", description: "Gruplar, umutlar, sosyal çevre", sign: "Kova" },
    { house: 12, name: "Bilinçaltı", description: "Gizli düşmanlar, ruhsal gelişim", sign: "Balık" }
  ];

  const readingTypes = [
    {
      type: "Yıllık Genel Değerlendirme",
      description: "Tüm yaşam alanlarınızın bir yıllık durumunu gösterir",
      timing: "Doğum gününüzde veya yıl başında"
    },
    {
      type: "Hayat Dönemi Analizi",
      description: "Önemli geçiş dönemlerinde hangi alanların etkileneceği",
      timing: "Büyük değişimler öncesinde"
    },
    {
      type: "Kişisel Gelişim Haritası",
      description: "Hangi alanlarda odaklanmanız gerektiğini gösterir",
      timing: "Kendini geliştirme dönemlerinde"
    }
  ];

  const interpretationTips = [
    {
      tip: "Boş evlere dikkat",
      detail: "Kart çıkmayan evler de önemli - o alanlar şu an sakin veya gelişmeye açık"
    },
    {
      tip: "Element dağılımı",
      detail: "Hangi elementlerden çok kart var? Ateş=eylem, Su=duygu, Hava=düşünce, Toprak=pratik"
    },
    {
      tip: "Karşıt evler",
      detail: "1-7, 2-8, 3-9, 4-10, 5-11, 6-12 evleri birlikte değerlendirin - denge arayın"
    },
    {
      tip: "Major Arkana vurgusu",
      detail: "Major Arkana kartları hangi evde çıktıysa o alan kadersel önemde"
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Astrolojik Yayılım</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Astrolojik Yayılım</h1>
              <p className="text-purple-300 font-medium">İleri Seviye • 12 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            12 astroloji evini temsil eden kapsamlı spread. Hayatın tüm alanlarını detaylı analiz eder.
            Yıllık okumalar ve derin kişisel değerlendirmeler için ideal olan bu yöntem astroloji bilgisi gerektirir.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">45-90 dakika</p>
            <p className="text-slate-400 text-sm">Çok detaylı analiz</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Yaşam analizi</p>
            <p className="text-slate-400 text-sm">Tüm alanları kapsayan</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">İleri</p>
            <p className="text-slate-400 text-sm">Astroloji bilgisi gerekli</p>
          </div>
        </div>

        {/* Astrolojik Evler */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">12 Astrolojik Ev</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {astroHouses.map((house) => (
              <div key={house.house} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{house.house}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100">{house.name}</h3>
                    <p className="text-purple-300 text-xs">{house.sign}</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">{house.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Dizilim Şeması */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Astrolojik Dizilim Şeması</h2>
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
            <div className="grid grid-cols-4 gap-3 max-w-2xl mx-auto">
              {/* Saat yönünde dizilim */}
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">9<br/>Felsefe</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">10<br/>Kariyer</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">11<br/>Arkadaş</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">12<br/>Bilinçaltı</div>
              
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">8<br/>Dönüşüm</div>
              <div className="col-span-2 bg-purple-700 rounded-lg p-3 text-center text-white font-bold text-sm flex items-center justify-center">Merkez<br/>Genel Enerji</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">1<br/>Kimlik</div>
              
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">7<br/>Ortaklık</div>
              <div className="col-span-2 bg-purple-500 rounded-lg p-3 text-center text-white font-bold text-sm flex items-center justify-center">İç Çember<br/>Kişisel Gelişim</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">2<br/>Değerler</div>
              
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">6<br/>Sağlık/İş</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">5<br/>Yaratıcılık</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">4<br/>Ev/Aile</div>
              <div className="bg-purple-600 rounded-lg p-3 text-center text-white font-bold text-sm">3<br/>İletişim</div>
            </div>
          </div>
        </div>

        {/* Okuma Türleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Okuma Türleri</h2>
          <div className="space-y-6">
            {readingTypes.map((reading, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-2 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-purple-400" />
                  {reading.type}
                </h3>
                <p className="text-slate-300 mb-2">{reading.description}</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-400" />
                  <p className="text-slate-400 text-sm italic">{reading.timing}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yorumlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationTips.map((tip, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-400" />
                  {tip.tip}
                </h3>
                <p className="text-slate-300">{tip.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Adım Adım Süreç */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Okuma Süreci</h2>
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Astroloji Temellerini Bilin</h3>
                  <p className="text-slate-300 mb-2">
                    12 evin anlamlarını ve bunların yaşamınızdaki karşılığını iyi bilin.
                    Her evin hangi yaşam alanını temsil ettiğini hatırlayın.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartları Sırayla Yerleştirin</h3>
                  <p className="text-slate-300 mb-2">
                    1. evden başlayarak saat yönünde 12. eve kadar kartları yerleştirin.
                    Her kartı yerleştirirken o evin anlamını düşünün.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Genel Resmi Görün</h3>
                  <p className="text-slate-300 mb-2">
                    Hangi evlerde güçlü kartlar var? Hangi yaşam alanları vurgulanıyor?
                    Element dağılımı nasıl? Major/Minor Arkana oranı nedir?
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Detaylı Analiz</h3>
                  <p className="text-slate-300">
                    Her evi ayrı ayrı yorumlayın. Sonra karşıt evleri karşılaştırın (1-7, 2-8, vb.).
                    Son olarak genel bir sentez yaparak yaşam planınızı oluşturun.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Uyarılar */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Uyarılar</h2>
          <div className="space-y-4">
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">🎓 Astroloji Bilgisi Gerekli</h3>
              <p className="text-amber-100">
                Bu spread astroloji temellerini bilmeyi gerektirir. 12 evin anlamlarını 
                öğrenmeden kullanmayın.
              </p>
            </div>
            
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⏰ Zaman Ayırın</h3>
              <p className="text-amber-100">
                Bu okuma çok uzun sürer. Acele etmeyin, her evi dikkatli analiz edin.
                Birkaç oturumda tamamlayabilirsiniz.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">📝 Notlar Alın</h3>
              <p className="text-amber-100">
                Her evin yorumunu yazın. Bu okumayı aylarca referans olarak kullanabilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/karar-verme"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Karar Verme
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/zaman-cizelgesi"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Zaman Çizelgesi
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}